import get from 'lodash/get';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import styled from 'styled-components';

import FormContext from 'src/presentation/contexts/FormContext';
import { getNumberValue } from '@ad-scripts/presentation/components/common/input_field';
import InputField from 'src/presentation/components/forms/InputField';

type NumberInputFieldProps = {
  attr: string;
  limitDecimals?: number; // If set to 0, no decimals allowed
  convertToDisplayFn?: Function;
  convertFromDisplayFn?: Function;
  onUpdate: Function;
};

const NumberInputField = (props: NumberInputFieldProps): JSX.Element => {
  const { attr, convertToDisplayFn, convertFromDisplayFn, limitDecimals, onUpdate, ...otherProperties } = props;
  const { workingState } = useContext(FormContext);

  const getAttributeValue = useCallback(() => {
    const value = get(workingState, attr, 0);
    const translatedValue = convertToDisplayFn ? convertToDisplayFn(value) : Number(value);
    return translatedValue.toString();
  }, [workingState, attr, convertToDisplayFn]);

  const [input, setInput] = useState(getAttributeValue());

  useEffect(() => {
    setInput(getAttributeValue());
  }, [limitDecimals]); // eslint-disable-line react-hooks/exhaustive-deps

  const onChangeHandler = (value = '') => {
    const [numberValue, isValid] = getNumberValue(value);

    if (!isValid || (limitDecimals != undefined && exceedsAllowablePrecision(value, limitDecimals))) {
      return false;
    }

    setInput(value);

    const conditionValue = convertFromDisplayFn ? convertFromDisplayFn(numberValue) : Number(numberValue);
    onUpdate(conditionValue);
  };

  const onBlur = evt => {
    const value = parseFloat(evt.target.value);
    if (!value) {
      setInput('0');
    }
  };

  return <StyledInputField attr={attr} {...otherProperties} onChange={onChangeHandler} value={input} onBlur={onBlur} />;
};

function exceedsAllowablePrecision(value, precision) {
  if (!value) {
    return false;
  }

  if (precision === 0 && value.includes('.')) {
    return true;
  }

  return (value.split('.')[1] || []).length > precision;
}

const StyledInputField = styled(InputField)`
  border-color: ${p => p.theme.colors.gray400};
`;

export default NumberInputField;
